import { ReactNode } from 'react'

import { Widget } from '@factorialco/f0-react/dist/experimental'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { BarChartWrapper } from './BarChartWrapper'
import { CategoryBarChartWrapper } from './CategoryBarChartWrapper'
import { FinanceEmoji, FinanceEmpetyChartWidget } from './FinanceEmptyChartWidget'
import { PieChartWrapper } from './PieChartWrapper'
import { ChartData, SupportedColors } from './types'

export enum SupportedCharts {
  LINE = 'categoryBarChart',
  PIE = 'pieChart',
  COUNTERS = 'counters',
  BAR_CHART_STACKED = 'barChartStacked',
  BAR_CHART_STACKED_BY_SIGN = 'barChartStackedBySign',
  BAR_CHART = 'barChart',
}

export type { SupportedColors, ChartData }

interface BarChartConfig {
  displayAxis?: boolean
  barLabels?: { [key: string]: { label: string; color?: SupportedColors } }
}

interface FinanceWidgetPropsCommon {
  id: string
  title?: string
  type: SupportedCharts
  showChart: boolean
  countersChartDatas?: ChartData[]
  overviewChartData?: ChartData
  chartDatas?: ChartData[]
  linkData?: LinkData
  displayedFilters?: (string | string[])[]
  tickFormatter?: (value: string) => string
  barChartConfig?: BarChartConfig
  aspect?: 'small' | 'wide'
  children?: ReactNode
}

interface FinanceWidgetPropsNotEmptyState extends FinanceWidgetPropsCommon {
  type:
    | SupportedCharts.COUNTERS
    | SupportedCharts.BAR_CHART_STACKED_BY_SIGN
    | SupportedCharts.BAR_CHART_STACKED
  emptyState?: null
}

interface FinanceWidgetPropsWithChart extends FinanceWidgetPropsCommon {
  type: Exclude<
    SupportedCharts,
    | SupportedCharts.COUNTERS
    | SupportedCharts.BAR_CHART_STACKED_BY_SIGN
    | SupportedCharts.BAR_CHART_STACKED
  >
  emptyState: {
    icon: FinanceEmoji
    text: string
    description?: string
  }
}

export type FinanceWidgetProps = FinanceWidgetPropsNotEmptyState | FinanceWidgetPropsWithChart

type LinkData = {
  title: string
  url: string
}

type ChartDataWithNumberValue = Omit<ChartData, 'value'> & {
  value: number
}

type ChartDataWithHashValue = Omit<ChartData, 'value'> & {
  value: { [key: string]: number }
}

export const FinanceWidget = ({
  id,
  title,
  type,
  showChart,
  countersChartDatas,
  overviewChartData,
  linkData,
  displayedFilters,
  tickFormatter,
  emptyState,
  aspect = 'small',
  barChartConfig = {},
  chartDatas = [],
  children,
}: FinanceWidgetProps) => {
  const fetchTickFormatter = (formatter: ((value: string) => string) | undefined) =>
    formatter === undefined ? (value: string) => value : formatter

  const isEmpty = useUnsafeMemo(() => isChartDataEmpty(chartDatas), [chartDatas])

  const chart = (chartType: SupportedCharts) => {
    if (!!emptyState && isEmpty) {
      return (
        <FinanceEmpetyChartWidget
          icon={emptyState.icon}
          text={emptyState.text}
          description={emptyState.description}
        />
      )
    }
    switch (chartType) {
      case SupportedCharts.COUNTERS:
        return <></>
      case SupportedCharts.PIE:
        return (
          <PieChartWrapper
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            totalChartData={overviewChartData as ChartDataWithNumberValue}
            groupedChartData={(chartDatas || []).filter(
              (data): data is ChartDataWithNumberValue => typeof data.value === 'number'
            )}
            tickFormatter={fetchTickFormatter(tickFormatter)}
          />
        )
      case SupportedCharts.LINE:
        return (
          <CategoryBarChartWrapper
            groupedChartData={(chartDatas || []).filter(
              (data): data is ChartDataWithNumberValue => typeof data.value === 'number'
            )}
          />
        )
      case SupportedCharts.BAR_CHART_STACKED_BY_SIGN:
        return (
          <BarChartWrapper
            groupedChartData={(chartDatas || []).filter(
              (data): data is ChartDataWithHashValue => typeof data.value === 'object'
            )}
            barLabels={barChartConfig.barLabels || {}}
            tickFormatter={fetchTickFormatter(tickFormatter)}
            type={'stacked-by-sign'}
            aspect={aspect}
            displayAxis={barChartConfig.displayAxis}
          />
        )
      case SupportedCharts.BAR_CHART:
        return (
          <BarChartWrapper
            groupedChartData={chartDatas.filter(
              (data): data is ChartDataWithHashValue => typeof data.value === 'object'
            )}
            barLabels={barChartConfig.barLabels || {}}
            tickFormatter={fetchTickFormatter(tickFormatter)}
            aspect={aspect}
            type={'simple'}
            displayAxis={barChartConfig.displayAxis}
          />
        )
      case SupportedCharts.BAR_CHART_STACKED:
        return (
          <BarChartWrapper
            groupedChartData={chartDatas.filter(
              (data): data is ChartDataWithHashValue => typeof data.value === 'object'
            )}
            barLabels={barChartConfig.barLabels || {}}
            tickFormatter={fetchTickFormatter(tickFormatter)}
            type={'stacked'}
            aspect={aspect}
            displayAxis={barChartConfig.displayAxis}
          />
        )
    }
  }

  const subtitle =
    displayedFilters?.map((v) => (Array.isArray(v) ? v.join(' - ') : v)).join(' | ') || ''

  const onLickClickHandler = (widgetId: string, url: string) => {
    track(trackingEvents.accounting.widgets.clickTitleLink, { widget_id: widgetId, url: url })
  }

  return (
    <Widget
      header={
        title || subtitle || linkData
          ? {
              link: linkData
                ? {
                    title: linkData.title,
                    url: linkData.url,
                    onClick: () => onLickClickHandler(id, linkData.url),
                  }
                : undefined,
              title: title,
              subtitle: subtitle,
            }
          : undefined
      }
      summaries={
        isEmpty &&
        type !== SupportedCharts.COUNTERS &&
        type !== SupportedCharts.BAR_CHART_STACKED_BY_SIGN &&
        type !== SupportedCharts.BAR_CHART_STACKED
          ? []
          : (countersChartDatas || []).map((d) => ({
              label: d.label,
              value: d.formattedValue || '',
            }))
      }
    >
      {showChart ? chart(type) : <></>}
      {children}
    </Widget>
  )
}

const isChartDataEmpty = (chartDatas: ChartData[]) => {
  if (!chartDatas) return true
  if (chartDatas.length === 0) return true
  return chartDatas.every((data) =>
    typeof data.value === 'number' ? data.value === 0 : allValuesZero(data.value)
  )
}

const allValuesZero = (value: { [key: string]: number }) => {
  return Object.values(value).every((v) => v === 0)
}
